import { Tooltip } from '@/ui'
import ctl from '@netlify/classnames-template-literals'
import { memo } from 'react'
import type { LayerType } from '../data/v1'

const TYPE_STYLES: Record<LayerType, string> = {
	content: 'bg-blue-100 text-blue-700',
	backdrop: 'bg-purple-100 text-purple-700',
	substack: 'bg-amber-100 text-amber-700',
	text: 'bg-emerald-100 text-emerald-700',
	shape: 'bg-pink-100 text-pink-700',
	image: 'bg-cyan-100 text-cyan-700',
}

const TYPE_LABELS: Record<LayerType, string> = {
	content: 'C',
	backdrop: 'B',
	substack: 'S',
	text: 'T',
	shape: 'R',
	image: 'I',
}

export const TypeBadge = memo(function TypeBadge({ type }: { type: LayerType }) {
	return (
		<span
			className={ctl(`
				inline-flex items-center justify-center size-4 rounded text-[9px] font-bold leading-none shrink-0
				${TYPE_STYLES[type]}
			`)}
		>
			{TYPE_LABELS[type]}
		</span>
	)
})

export const LockToggle = memo(
	function LockToggle(
		{ locked, inherited, onClick }: { locked: boolean; inherited: boolean; onClick: (e: React.MouseEvent) => void },
	) {
		const showLockedIcon = locked || inherited
		return (
			<Tooltip content={inherited ? 'Locked by parent' : locked ? 'Unlock layer' : 'Lock layer'}>
				<button
					type="button"
					onClick={onClick}
					className={ctl(`
					flex size-4 shrink-0 items-center justify-center rounded outline-none focus-visible:ring-1 focus-visible:ring-blue-400
					${inherited ? 'cursor-default' : 'cursor-pointer'}
					${
						showLockedIcon
							? 'text-grey-500 hover:text-grey-700'
							: 'text-grey-300 opacity-0 group-hover/layer-row:opacity-100 hover:text-grey-500'
					}
				`)}
				>
					{showLockedIcon
						? (
							<svg width="12" height="12" viewBox="0 0 16 16" fill="none">
								<rect
									x="3"
									y="7"
									width="10"
									height="7"
									rx="1.5"
									stroke="currentColor"
									strokeWidth="1.3"
								/>
								<path
									d="M5 7V5a3 3 0 016 0v2"
									stroke="currentColor"
									strokeWidth="1.3"
									strokeLinecap="round"
								/>
							</svg>
						)
						: (
							<svg width="12" height="12" viewBox="0 0 16 16" fill="none">
								<rect
									x="3"
									y="7"
									width="10"
									height="7"
									rx="1.5"
									stroke="currentColor"
									strokeWidth="1.3"
								/>
								<path
									d="M5 7V5a3 3 0 016 0"
									stroke="currentColor"
									strokeWidth="1.3"
									strokeLinecap="round"
								/>
							</svg>
						)}
				</button>
			</Tooltip>
		)
	},
)

export const VisibilityToggle = memo(
	function VisibilityToggle(
		{ hidden, inherited, onClick }: { hidden: boolean; inherited: boolean; onClick: (e: React.MouseEvent) => void },
	) {
		const showHiddenIcon = hidden || inherited
		return (
			<Tooltip content={inherited ? 'Hidden by parent' : hidden ? 'Show layer' : 'Hide layer'}>
				<button
					type="button"
					onClick={onClick}
					className={ctl(`
					flex size-4 shrink-0 items-center justify-center rounded outline-none focus-visible:ring-1 focus-visible:ring-blue-400
					${inherited ? 'cursor-default' : 'cursor-pointer'}
					${
						showHiddenIcon
							? 'text-grey-500 hover:text-grey-700'
							: 'text-grey-300 opacity-0 group-hover/layer-row:opacity-100 hover:text-grey-500'
					}
				`)}
				>
					{showHiddenIcon
						? (
							<svg width="12" height="12" viewBox="0 0 16 16" fill="none">
								<path
									d="M2 2l12 12M6.5 6.5a2 2 0 002.83 2.83M3.5 5.5C2.5 6.5 1.5 8 1.5 8s2.5 4.5 6.5 4.5c1 0 1.9-.3 2.7-.7M12.5 10.5c1-1 2-2.5 2-2.5S12 3.5 8 3.5c-.5 0-1 .05-1.5.15"
									stroke="currentColor"
									strokeWidth="1.3"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						)
						: (
							<svg width="12" height="12" viewBox="0 0 16 16" fill="none">
								<path
									d="M1.5 8s2.5-4.5 6.5-4.5S14.5 8 14.5 8s-2.5 4.5-6.5 4.5S1.5 8 1.5 8z"
									stroke="currentColor"
									strokeWidth="1.3"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.3" />
							</svg>
						)}
				</button>
			</Tooltip>
		)
	},
)
